import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Stack,
  Avatar,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Folder as FolderIcon,
  CloudDownload as CloudDownloadIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { assetsAPI } from '../../services/api';

const AssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalAssets, setTotalAssets] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    websiteId: '',
    category: '',
    fileType: '',
    search: ''
  });
  
  // UI States
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedAssetForMenu, setSelectedAssetForMenu] = useState(null);
  
  // Bulk operations
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchAssets();
    fetchAnalytics();
  }, [page, rowsPerPage, filters]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      };

      const response = await assetsAPI.getAssets(params);
      setAssets(response.data.assets);
      setWebsites(response.data.websites);
      setTotalAssets(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await assetsAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleViewAsset = (asset) => {
    setSelectedAsset(asset);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (asset) => {
    setAssetToDelete(asset);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await assetsAPI.deleteAsset(assetToDelete._id);
      toast.success('Asset deleted successfully');
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
      fetchAssets();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Failed to delete asset');
    }
  };

  const handleMenuClick = (event, asset) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAssetForMenu(asset);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAssetForMenu(null);
  };

  const handleSelectAsset = (assetId) => {
    setSelectedAssets(prev => {
      if (prev.includes(assetId)) {
        return prev.filter(id => id !== assetId);
      } else {
        return [...prev, assetId];
      }
    });
  };

  const handleSelectAllAssets = (event) => {
    if (event.target.checked) {
      setSelectedAssets(assets.map(a => a._id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const response = await assetsAPI.bulkDeleteAssets(selectedAssets);
      toast.success(`${response.data.deletedCount} assets deleted successfully`);
      setBulkDeleteDialogOpen(false);
      setSelectedAssets([]);
      fetchAssets();
      fetchAnalytics();
    } catch (error) {
      console.error('Error bulk deleting assets:', error);
      toast.error('Failed to delete assets');
    }
  };

  const getFileIcon = (category) => {
    switch (category) {
      case 'image':
        return <ImageIcon color="primary" />;
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'document':
      case 'spreadsheet':
        return <DocumentIcon color="info" />;
      default:
        return <AttachFileIcon />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'image':
        return 'primary';
      case 'pdf':
        return 'error';
      case 'document':
        return 'info';
      case 'spreadsheet':
        return 'success';
      case 'text':
        return 'warning';
      default:
        return 'default';
    }
  };

  const clearFilters = () => {
    setFilters({
      websiteId: '',
      category: '',
      fileType: '',
      search: ''
    });
    setPage(0);
  };

  if (loading && assets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading assets...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Assets & Files
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchAssets();
            fetchAnalytics();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Analytics Cards */}
      {!analyticsLoading && analytics && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <AttachFileIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{analytics.totalFiles}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Files
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <StorageIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{analytics.totalSizeFormatted}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Size
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <CloudDownloadIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{analytics.storageUsage.percentage}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Storage Used
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={analytics.storageUsage.percentage} 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <FolderIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{analytics.websiteBreakdown.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Websites
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category Breakdown */}
      {!analyticsLoading && analytics && Object.keys(analytics.categoryBreakdown).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="File Categories" />
          <CardContent>
            <Grid container spacing={2}>
              {Object.entries(analytics.categoryBreakdown).map(([category, count]) => (
                <Grid item xs={6} sm={4} md={2} key={category}>
                  <Box textAlign="center">
                    {getFileIcon(category)}
                    <Typography variant="h6">{count}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Filters"
          action={
            <Button size="small" onClick={clearFilters}>
              Clear All
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Website</InputLabel>
                <Select
                  value={filters.websiteId}
                  onChange={(e) => handleFilterChange('websiteId', e.target.value)}
                  label="Website"
                >
                  <MenuItem value="">All Websites</MenuItem>
                  {websites.map((website) => (
                    <MenuItem key={website._id} value={website._id}>
                      {website.name} ({website.siteId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="image">Images</MenuItem>
                  <MenuItem value="pdf">PDFs</MenuItem>
                  <MenuItem value="document">Documents</MenuItem>
                  <MenuItem value="spreadsheet">Spreadsheets</MenuItem>
                  <MenuItem value="text">Text Files</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={filters.fileType}
                  onChange={(e) => handleFilterChange('fileType', e.target.value)}
                  label="File Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="image/jpeg">JPEG</MenuItem>
                  <MenuItem value="image/png">PNG</MenuItem>
                  <MenuItem value="image/gif">GIF</MenuItem>
                  <MenuItem value="image/webp">WebP</MenuItem>
                  <MenuItem value="image/svg+xml">SVG</MenuItem>
                  <MenuItem value="application/pdf">PDF</MenuItem>
                  <MenuItem value="application/msword">Word Doc</MenuItem>
                  <MenuItem value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">Word (DOCX)</MenuItem>
                  <MenuItem value="application/vnd.ms-excel">Excel</MenuItem>
                  <MenuItem value="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">Excel (XLSX)</MenuItem>
                  <MenuItem value="text/plain">Text</MenuItem>
                  <MenuItem value="text/csv">CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by filename..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardHeader
          title="Assets"
          action={
            selectedAssets.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDeleteClick}
                size="small"
              >
                Delete Selected ({selectedAssets.length})
              </Button>
            )
          }
        />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedAssets.length > 0 && selectedAssets.length < assets.length}
                    checked={assets.length > 0 && selectedAssets.length === assets.length}
                    onChange={handleSelectAllAssets}
                  />
                </TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Website</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No assets found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assets.map((asset) => (
                  <TableRow key={asset._id} hover selected={selectedAssets.includes(asset._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAssets.includes(asset._id)}
                        onChange={() => handleSelectAsset(asset._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {asset.category === 'image' ? (
                          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                            <img
                              src={asset.downloadUrl}
                              alt={asset.originalName}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.querySelector('.fallback-icon').style.display = 'flex';
                              }}
                            />
                            <Box
                              className="fallback-icon"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              {getFileIcon(asset.category)}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            {getFileIcon(asset.category)}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ mr: 2 }}>
                          {getFileIcon(asset.category)}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {asset.originalName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {asset.mimeType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {asset.website.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {asset.website.siteId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.category}
                        size="small"
                        color={getCategoryColor(asset.category)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {asset.humanReadableSize || `${Math.round(asset.size / 1024)} KB`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(asset.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(asset.createdAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => window.open(asset.downloadUrl, '_blank')}
                        title="Download"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, asset)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalAssets}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewAsset(selectedAssetForMenu)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => window.open(selectedAssetForMenu?.downloadUrl, '_blank')}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteClick(selectedAssetForMenu)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Asset Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Asset Details</DialogTitle>
        <DialogContent>
          {selectedAsset && <AssetDetails asset={selectedAsset} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The file will be permanently deleted from Backblaze B2 storage.
          </Alert>
          <Typography>
            Are you sure you want to delete "{assetToDelete?.originalName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Multiple Assets</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All selected files will be permanently deleted from Backblaze B2 storage.
          </Alert>
          <Typography>
            Are you sure you want to delete {selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete {selectedAssets.length} Asset{selectedAssets.length > 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Asset Details Component
const AssetDetails = ({ asset }) => {
  return (
    <Box>
      <Grid container spacing={3}>
        {/* File Preview */}
        {asset.category === 'image' && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader title="Preview" />
              <CardMedia
                component="img"
                height="200"
                image={asset.downloadUrl}
                alt={asset.originalName}
                sx={{ objectFit: 'contain' }}
              />
            </Card>
          </Grid>
        )}

        {/* File Information */}
        <Grid item xs={12} md={asset.category === 'image' ? 6 : 12}>
          <Card variant="outlined">
            <CardHeader title="File Information" />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Original Name
                  </Typography>
                  <Typography variant="body1">
                    {asset.originalName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    File Size
                  </Typography>
                  <Typography variant="body1">
                    {asset.humanReadableSize || `${Math.round(asset.size / 1024)} KB`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    MIME Type
                  </Typography>
                  <Typography variant="body1">
                    {asset.mimeType}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Chip
                    label={asset.category}
                    size="small"
                    color={asset.category === 'image' ? 'primary' : 'default'}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Website Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader title="Website Information" />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Website Name
                  </Typography>
                  <Typography variant="body1">
                    {asset.website.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Site ID
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {asset.website.siteId}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader title="Upload Information" />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(asset.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Form Submission
                  </Typography>
                  <Typography variant="body1">
                    {new Date(asset.formSubmission.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Download URL
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(asset.downloadUrl, '_blank')}
                  >
                    Download File
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssetsPage; 